import React, { useState } from "react";
import { Accordion } from "components/Drawer";
import { BackdropSection } from "components";
import { useDevicesAndUsers } from "api/actions/util";
import { CopyToClipboard } from "react-copy-to-clipboard";
import { FaCopy, FaCheck } from "react-icons/fa";

export const DeviceUserList = () => {
  const { usersData, devicesByUser, loading } = useDevicesAndUsers();

  if (loading) return <BackdropSection open={true} />;

  const CopyableValue = ({ label, value, large = false }) => {
    const [copied, setCopied] = useState(false);

    const handleCopy = () => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    };

    return (
      <div className="space-y-1">
        <p className="text-[1.01rem] font-medium text-gray-500 dark:text-gray-400">
          {label}
        </p>
        <div className="flex items-start gap-2 group">
          <p
            className={`${
              large ? "text-xl" : "text-base"
            } flex-1 break-all text-gray-900 dark:text-gray-200`}
          >
            {value || "N/A"}
          </p>
          {value && value !== "N/A" && (
            <CopyToClipboard text={value} onCopy={handleCopy}>
              <button
                className="p-1 transition-opacity rounded opacity-0 hover:bg-gray-100 group-hover:opacity-100 dark:hover:bg-gray-700"
                title={copied ? "Copied!" : "Copy to clipboard"}
              >
                {copied ? (
                  <FaCheck className="w-4 h-4 text-green-500" />
                ) : (
                  <FaCopy className="w-4 h-4 text-gray-500 dark:text-gray-400" />
                )}
              </button>
            </CopyToClipboard>
          )}
        </div>
      </div>
    );
  };

  const DeviceDetails = ({ device, users }) => {
    const formatDate = (dateString) => {
      return new Date(dateString).toLocaleString();
    };

    return (
      <div className="p-4 space-y-4 bg-white border border-gray-200 rounded-lg shadow-sm dark:border-gray-700 dark:bg-gray-900">
        <div className="flex items-center justify-between pb-3 border-b dark:border-gray-700">
          <div className="flex items-center space-x-3">
            <div className="p-2 bg-blue-100 rounded-full dark:bg-blue-900">
              {device?.osName?.toLowerCase().includes("mac") ? (
                <svg
                  className="w-5 h-5 text-blue-600 dark:text-blue-300"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="2"
                    d="M9.75 17L9 20l-1 1h8l-1-1-.75-3M3 13h18M5 17h14a2 2 0 002-2V5a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"
                  />
                </svg>
              ) : (
                <svg
                  className="w-5 h-5 text-blue-600 dark:text-blue-300"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="2"
                    d="M9.75 17L9 20l-1 1h8l-1-1-.75-3M3 13h18M5 17h14a2 2 0 002-2V5a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"
                  />
                </svg>
              )}
            </div>
            <div>
              <h3 className="font-medium text-gray-900 dark:text-white">
                {device?.deviceId}
              </h3>
              <p className="text-sm text-gray-500 dark:text-gray-400">
                {device?.osName}
              </p>
            </div>
          </div>
          <span
            className={`rounded-full px-2.5 py-0.5 text-xs font-medium ${
              device?.action === "add"
                ? "bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-300"
                : "bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300"
            }`}
          >
            {device?.action || "Unknown"}
          </span>
        </div>
        <div className="grid grid-cols-1 gap-4">
          <CopyableValue
            label="Base device hash"
            value={device?.baseDeviceHash}
          />

          <CopyableValue label="Current Browser" value={device?.browserName} />
          <CopyableValue
            label="Assigned on"
            value={formatDate(device?.createdAt)}
          />
          <CopyableValue
            label="Device Status"
            value={device?.isActive ? "Active" : "Inactive"}
          />
          <CopyableValue
            label="Current User"
            value={users[device?.currentUserId]?.displayName || "Unknown User"}
          />
          <CopyableValue label="Device ID" value={device?.deviceId} />
          <CopyableValue
            label="Last used by"
            value={
              users[device?.invalidatedBy]?.displayName ||
              users[device?.currentUserId]?.displayName ||
              "Unknown User"
            }
          />

          <CopyableValue
            label="Last Login"
            value={formatDate(device?.lastLoginAt)}
          />
          <CopyableValue label="Operating system" value={device?.platform} />
        </div>
        <div className="pt-3 border-t dark:border-gray-700">
          <h4 className="mb-2 text-sm font-medium text-gray-900 dark:text-white">
            Recent Users
          </h4>
          <div className="flex flex-wrap gap-2">
            {device.recentUsers?.map((userId) => (
              <span
                key={userId}
                className="inline-flex items-center rounded-full bg-blue-100 px-2.5 py-0.5 text-sm font-medium text-blue-800 dark:bg-blue-900 dark:text-blue-300"
              >
                {users[userId]?.displayName || "Unknown User"}
              </span>
            ))}
          </div>
        </div>
        <div className="pt-3 text-xs text-gray-500 border-t dark:border-gray-700 dark:text-gray-400">
          <div className="flex justify-between">
            <span>Created: {formatDate(device?.createdAt)}</span>
            <span>Updated: {formatDate(device?.updatedAt)}</span>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="space-y-4 ">
      {Object.entries(devicesByUser).map(([userId, userDevices]) => (
        <Accordion
          key={userId}
          title={`${usersData[userId]?.displayName || "Unknown"} (${
            userDevices.length
          } devices)`}
          content={
            <div className="max-h-[60vh] space-y-4 overflow-y-auto">
              {userDevices.map((device) => (
                <DeviceDetails
                  key={device.id}
                  device={device}
                  users={usersData}
                />
              ))}
            </div>
          }
        />
      ))}
    </div>
  );
};
